import { useEffect, useRef, useState } from 'react';
import { moedaBr } from '../estado/gerador.js';
import { lerComissao, type OpcaoComissao } from '../lib/numero.js';
import './SeletorComissao.css';

/** Piso da largura da lista: no desktop o campo tem ~110px e 'R$ 100,00' nao cabe. */
const LARGURA_MIN_LISTA = 190;

/**
 * O campo de comissão do item — S4.
 *
 * ## Por que um campo, e não um `<select>`
 *
 * O cliente pediu para **digitar** o percentual, não escolher de uma lista
 * fechada. A razão dele tem fundamento: um dropdown de seis esconde que aquilo
 * é uma **escala**, e obriga a decorar qual ponta é a barata.
 *
 * ## Por que a lista continua existindo
 *
 * Porque o preço de cada comissão é a informação que decide a venda. Num campo
 * puramente digitável o vendedor teria de digitar 4%, salvar, ler o preço,
 * digitar 4,5%, salvar de novo — para comparar duas colunas. O seletor do del
 * credere já mostra o preço de cada opção exatamente por isso.
 *
 * Então: digita-se livremente, e a lista mostra o que cada escolha custa.
 *
 * ## Por que NÃO é `<datalist>`
 *
 * Seria a versão barata disto, em dez linhas. Mas em mobile o suporte é pobre e
 * inconsistente — e este é um PWA mobile-first —, não dá para mostrar o preço
 * de forma confiável (cada navegador trata `label`/`value` de um jeito) e não
 * dá para estilizar. O projeto tem CSS próprio e **nenhum `datalist`**; este
 * componente segue o molde do `BuscaProduto`, que já é o combobox da casa.
 *
 * ## A coluna sem preço APARECE
 *
 * Marcada e desabilitada, com o motivo. Some da lista → o vendedor conclui que
 * aquela comissão não existe e vai procurá-la em outro lugar. É a mesma decisão
 * que o `BuscaProduto` tomou para produto sem preço, e pelo mesmo motivo.
 */

export interface SeletorComissaoProps {
  /** As comissões do quadrante, na ordem da escala, com preço ou `null`. */
  opcoes: readonly OpcaoComissao[];
  /** O nível escolhido hoje, ou `null`/`undefined` quando vale a regra. */
  valor: string | null | undefined;
  /**
   * Chamado quando uma comissão válida é escolhida. Devolve `null` se gravou,
   * ou a mensagem da recusa.
   *
   * ## Por que devolve, em vez de só avisar
   *
   * Ao contrário do `<select>` do del credere, este campo tem **rascunho
   * local**: o texto que a pessoa digitou. Se o servidor recusa e ninguém
   * conta, a tela fica exibindo uma comissão que o servidor não tem — e o
   * vendedor emite (imutável!) confiando no que lê. Foi o que um teste pegou.
   */
  aoEscolher: (nivel: string) => Promise<string | null>;
  desabilitado?: boolean;
  /** Vai para o `aria-label` — a tabela tem uma linha por item. */
  descricao: string;
  /** Texto de quando nada foi escolhido: o padrão do orçamento, ou a regra. */
  textoPadrao?: string;
}

export function SeletorComissao({
  opcoes,
  valor,
  aoEscolher,
  desabilitado,
  descricao,
  textoPadrao,
}: SeletorComissaoProps): React.JSX.Element {
  const escolhida = opcoes.find((o) => o.nivel === valor);
  /*
   * O rascunho local é o TEXTO digitado; a verdade é o `valor` do servidor.
   *
   * Quando o servidor muda (recusa, ou outra aba), o campo volta sozinho — é o
   * mesmo contrato das células de dinheiro do `LinhaItem`: nunca exibir um
   * número que o servidor não tem, porque o vendedor emite (imutável!)
   * confiando no que lê.
   */
  const [texto, setTexto] = useState(escolhida?.rotulo ?? '');
  const [aberto, setAberto] = useState(false);
  const [aviso, setAviso] = useState<string | undefined>(undefined);
  const ultimoValor = useRef(valor);
  const entrada = useRef<HTMLInputElement>(null);
  /**
   * Onde desenhar a lista, em coordenadas de VIEWPORT.
   *
   * ## Por que `fixed` e não `absolute`
   *
   * A tabela de itens vive dentro de `.editor__rolagem`, que tem
   * `overflow: auto`. Uma lista `absolute` seria **cortada** por esse contêiner
   * — e pior justamente na última linha, que é a do item recém-adicionado, o
   * caso mais comum de mexer na comissão.
   *
   * O `<select>` do del credere não sofre disso porque o popup nativo é
   * desenhado pelo navegador fora do fluxo; trocá-lo por uma lista própria traz
   * o problema junto. `fixed` escapa de qualquer ancestral com overflow.
   */
  const [caixa, setCaixa] = useState<{ top: number; left: number; width: number } | undefined>(
    undefined,
  );

  /** Mede o campo e ancora a lista logo abaixo dele. */
  function medir(): void {
    const r = entrada.current?.getBoundingClientRect();
    if (r === undefined) return;
    setCaixa({ top: r.bottom + 4, left: r.left, width: r.width });
  }

  /*
   * Enquanto aberta, a lista acompanha rolagem e redimensionamento. O `true` no
   * terceiro argumento é a captura: sem ela, a rolagem do contêiner interno
   * (que não borbulha) deixaria a lista parada no lugar antigo.
   */
  useEffect(() => {
    if (!aberto) return undefined;
    const aoMover = (): void => {
      medir();
    };
    window.addEventListener('scroll', aoMover, true);
    window.addEventListener('resize', aoMover);
    return () => {
      window.removeEventListener('scroll', aoMover, true);
      window.removeEventListener('resize', aoMover);
    };
  }, [aberto]);

  useEffect(() => {
    if (ultimoValor.current !== valor) {
      ultimoValor.current = valor;
      setTexto(opcoes.find((o) => o.nivel === valor)?.rotulo ?? '');
      setAviso(undefined);
    }
  }, [valor, opcoes]);

  /** Filtra pelo que já foi digitado — `4` mostra 4,5% e 4%. */
  const digitado = texto.trim().replace(/%/g, '').replace('.', ',');
  const visiveis =
    digitado === ''
      ? opcoes
      : opcoes.filter((o) => o.rotulo.replace(/%/g, '').startsWith(digitado));

  /** Grava e, se o servidor recusar, devolve o campo dizendo por quê. */
  async function gravar(nivel: string, rotulo: string): Promise<void> {
    setTexto(rotulo);
    setAviso(undefined);
    const erro = await aoEscolher(nivel);
    if (erro !== null) {
      setTexto(escolhida?.rotulo ?? '');
      setAviso(erro);
    }
  }

  function escolher(o: OpcaoComissao): void {
    if (o.preco === null) return;
    setAberto(false);
    if (o.nivel === valor) {
      setTexto(o.rotulo);
      setAviso(undefined);
      return;
    }
    void gravar(o.nivel, o.rotulo);
  }

  /**
   * Confirma no `blur` — como os campos de dinheiro, e ao contrário do
   * `<select>` do del credere.
   *
   * Um `<select>` só muda quando alguém escolhe; um campo de texto passa por
   * estados intermediários ("4," a caminho de "4,5"). Validar a cada tecla
   * acusaria erro no meio da digitação.
   */
  function confirmar(): void {
    if (texto.trim() === '' && valor == null) {
      setAviso(undefined);
      return;
    }
    // Sem mudança: não incomoda e não manda PATCH.
    if (escolhida !== undefined && texto.trim() === escolhida.rotulo) {
      setAviso(undefined);
      return;
    }

    const r = lerComissao(texto, opcoes);
    if (!r.ok) {
      setAviso(r.mensagem);
      setTexto(escolhida?.rotulo ?? '');
      return;
    }
    setAviso(undefined);
    if (r.nivel === valor) return;
    const alvo = opcoes.find((o) => o.nivel === r.nivel);
    void gravar(r.nivel, alvo?.rotulo ?? texto.trim());
  }

  return (
    /*
     * O fechamento é do CONTÊINER, não do input — mesma razão do
     * `BuscaProduto`: com `onBlur` no input a lista desmonta antes do `click`
     * chegar, e o navegador não dispara `click` quando o alvo do `mousedown`
     * sai do DOM. O sintoma seria o pior: clicar e nada acontecer, sem erro.
     */
    <div
      className="seletor-comissao"
      onBlur={(e) => {
        if (e.currentTarget.contains(e.relatedTarget)) return;
        setAberto(false);
        confirmar();
      }}
    >
      <input
        ref={entrada}
        className="seletor-comissao__entrada"
        type="text"
        inputMode="decimal"
        value={texto}
        disabled={desabilitado ?? opcoes.length === 0}
        placeholder={textoPadrao ?? 'padrão'}
        aria-label={`Comissão de ${descricao}`}
        aria-expanded={aberto}
        aria-invalid={aviso !== undefined}
        onChange={(e) => {
          setTexto(e.target.value);
          setAviso(undefined);
          medir();
          setAberto(true);
        }}
        onFocus={() => {
          medir();
          setAberto(true);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            setAberto(false);
            confirmar();
          } else if (e.key === 'Escape') {
            setTexto(escolhida?.rotulo ?? '');
            setAviso(undefined);
            setAberto(false);
          }
        }}
      />

      {aberto && visiveis.length > 0 && (
        <ul
          className="seletor-comissao__lista"
          role="listbox"
          style={
            caixa === undefined
              ? undefined
              : {
                  top: caixa.top,
                  left: caixa.left,
                  minWidth: Math.max(caixa.width, LARGURA_MIN_LISTA),
                }
          }
          aria-label="Comissões disponíveis"
          /*
           * `preventDefault` no `mousedown` impede o input de perder o foco —
           * sem isso o `blur` do contêiner dispara antes do `click`.
           */
          onMouseDown={(e) => {
            e.preventDefault();
          }}
        >
          {visiveis.map((o) => {
            const semPreco = o.preco === null;
            return (
              <li key={o.nivel}>
                <button
                  type="button"
                  role="option"
                  aria-selected={o.nivel === valor}
                  className={
                    semPreco
                      ? 'seletor-comissao__item seletor-comissao__item--sem-preco'
                      : 'seletor-comissao__item'
                  }
                  disabled={semPreco}
                  title={semPreco ? 'Este produto não tem preço nesta comissão.' : undefined}
                  onClick={() => {
                    escolher(o);
                  }}
                >
                  <span className="seletor-comissao__pct">{o.rotulo}</span>
                  {semPreco ? (
                    <span className="seletor-comissao__tag">sem preço</span>
                  ) : (
                    <span className="seletor-comissao__preco">{moedaBr(o.preco)}</span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {aviso !== undefined && (
        <span className="seletor-comissao__aviso" role="alert">
          {aviso}
        </span>
      )}
    </div>
  );
}
